import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../context/ThemeContext';
import { apiCall } from '../utils/api';
import {
  Rocket, Loader, RefreshCw, ChevronRight, ChevronDown, TrendingUp,
  Image, Type, Video, AlertTriangle, CheckCircle, AlertCircle, XCircle,
  BarChart3, DollarSign, Eye, MousePointer, Target, Zap, Lock,
  ArrowUpRight, Info, Layers, Star
} from 'lucide-react';

const AD_STRENGTH_CONFIG = {
  EXCELLENT: { label: 'Excellent', color: '#10b981', icon: CheckCircle, bg: 'rgba(16, 185, 129, 0.15)' },
  GOOD: { label: 'Good', color: '#3b82f6', icon: CheckCircle, bg: 'rgba(59, 130, 246, 0.15)' },
  AVERAGE: { label: 'Average', color: '#f59e0b', icon: AlertTriangle, bg: 'rgba(245, 158, 11, 0.15)' },
  POOR: { label: 'Poor', color: '#ef4444', icon: XCircle, bg: 'rgba(239, 68, 68, 0.15)' },
  UNSPECIFIED: { label: 'Unknown', color: '#6b7280', icon: AlertCircle, bg: 'rgba(107, 114, 128, 0.15)' }
};

const PERFORMANCE_LABEL_CONFIG = {
  BEST: { label: 'Best', color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)' },
  GOOD: { label: 'Good', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },
  LOW: { label: 'Low', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
  LEARNING: { label: 'Learning', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
  UNSPECIFIED: { label: 'Pending', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)' }
};

export default function PmaxOptimizer({ selectedAccount }) {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;

  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [assetGroups, setAssetGroups] = useState([]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [assets, setAssets] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [tips, setTips] = useState([]);
  const [days, setDays] = useState(30);

  const [loading, setLoading] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [error, setError] = useState(null);

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiCall(`/api/pmax/campaigns?days=${days}`);
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.data);
      } else {
        throw new Error(data.error || 'Failed to load PMax campaigns');
      }
    } catch (err) {
      if (err.status === 403 && err.message?.includes('PMax')) {
        setError('ADDON_REQUIRED');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const [groupError, setGroupError] = useState(null);
  const [assetError, setAssetError] = useState(null);

  const loadAssetGroups = async (campaignId) => {
    try {
      setLoadingGroups(true);
      setGroupError(null);
      const response = await apiCall(`/api/pmax/campaigns/${campaignId}/asset-groups?days=${days}`);
      const data = await response.json();
      if (data.success) {
        setAssetGroups(data.data);
      } else {
        setGroupError(data.error || 'Failed to load asset groups');
      }
    } catch (err) {
      console.error('Failed to load asset groups:', err);
      setGroupError(err.message || 'Failed to load asset groups');
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadAssets = async (campaignId, assetGroupId) => {
    try {
      setLoadingAssets(true);
      setAssetError(null);
      const response = await apiCall(`/api/pmax/campaigns/${campaignId}/asset-groups/${assetGroupId}/assets`);
      const data = await response.json();
      if (data.success) {
        setAssets(data);
        loadTips(data);
      } else {
        setAssetError(data.error || 'Failed to load assets');
      }
    } catch (err) {
      console.error('Failed to load assets:', err);
      setAssetError(err.message || 'Failed to load assets');
    } finally {
      setLoadingAssets(false);
    }
  };

  const loadPerformance = async (campaignId) => {
    try {
      setLoadingPerformance(true);
      const response = await apiCall(`/api/pmax/campaigns/${campaignId}/performance?days=${days}`);
      const data = await response.json();
      if (data.success) {
        setPerformanceData(data.data);
      }
    } catch (err) {
      console.error('Failed to load performance:', err);
    } finally {
      setLoadingPerformance(false);
    }
  };

  const loadTips = async (assetData) => {
    try {
      const group = assetGroups.find(g => g.id === expandedGroup);
      const params = new URLSearchParams({
        adStrength: group?.adStrength || 'UNSPECIFIED',
        headlines: assetData.assets?.headlines?.length || 0,
        descriptions: assetData.assets?.descriptions?.length || 0,
        images: assetData.assets?.images?.length || 0,
        videos: assetData.assets?.videos?.length || 0
      });
      const response = await apiCall(`/api/pmax/asset-strength-tips?${params}`);
      const data = await response.json();
      if (data.success) {
        setTips(data.tips);
      }
    } catch (err) {
      console.error('Failed to load tips:', err);
    }
  };

  const handleCampaignSelect = (campaign) => {
    setSelectedCampaign(campaign);
    setAssetGroups([]);
    setExpandedGroup(null);
    setAssets(null);
    setTips([]);
    loadAssetGroups(campaign.id);
    loadPerformance(campaign.id);
  };

  const handleGroupExpand = (groupId) => {
    if (expandedGroup === groupId) {
      setExpandedGroup(null);
      setAssets(null);
      setTips([]);
    } else {
      setExpandedGroup(groupId);
      loadAssets(selectedCampaign.id, groupId);
    }
  };

  const formatCurrency = (val) => `€${parseFloat(val || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatNumber = (val) => parseFloat(val || 0).toLocaleString('nl-NL');

  const cardStyle = {
    background: c.bg.secondary,
    borderRadius: 16,
    border: `1px solid ${c.border.primary}`,
    padding: 24,
    marginBottom: 16
  };

  const statCardStyle = {
    background: c.bg.tertiary,
    borderRadius: 12,
    border: `1px solid ${c.border.secondary}`,
    padding: 16,
    flex: 1,
    minWidth: 140
  };

  if (error === 'ADDON_REQUIRED') {
    return <UpsellScreen theme={theme} getToken={getToken} />;
  }

  if (loading) {
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite', color: c.brand.primary }} />
      </div>
    );
  }

  return (
    <div style={{ padding: 40, maxWidth: 1600, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 12,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Rocket size={22} color="white" />
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: c.text.primary, margin: 0 }}>
              PMax Optimizer
            </h1>
            <span style={{
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              color: 'white', fontSize: 11, fontWeight: 600,
              padding: '3px 10px', borderRadius: 20
            }}>PRO</span>
          </div>
          <p style={{ color: c.text.secondary, fontSize: 14, margin: 0 }}>
            Analyze and optimize your Performance Max campaigns, asset groups, and individual assets.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            style={{
              background: c.bg.tertiary, color: c.text.primary,
              border: `1px solid ${c.border.primary}`, borderRadius: 8,
              padding: '8px 12px', fontSize: 13, cursor: 'pointer'
            }}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={loadCampaigns}
            style={{
              background: c.bg.tertiary, color: c.text.primary,
              border: `1px solid ${c.border.primary}`, borderRadius: 8,
              padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {error && error !== 'ADDON_REQUIRED' && (
        <div style={{
          ...cardStyle, background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444',
          display: 'flex', alignItems: 'center', gap: 12
        }}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {campaigns.length === 0 && !loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 60 }}>
          <Rocket size={48} style={{ color: c.text.tertiary, marginBottom: 16 }} />
          <h3 style={{ color: c.text.primary, fontSize: 18, marginBottom: 8 }}>No Performance Max Campaigns Found</h3>
          <p style={{ color: c.text.secondary, fontSize: 14 }}>
            Create a Performance Max campaign in Google Ads to start using the PMax Optimizer.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: selectedCampaign ? '340px 1fr' : '1fr', gap: 24 }}>
          {/* Campaign List */}
          <div>
            <h3 style={{ color: c.text.primary, fontSize: 14, fontWeight: 600, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              PMax Campaigns ({campaigns.length})
            </h3>
            {campaigns.map(campaign => (
              <div
                key={campaign.id}
                onClick={() => handleCampaignSelect(campaign)}
                style={{
                  ...cardStyle,
                  padding: 16,
                  cursor: 'pointer',
                  borderColor: selectedCampaign?.id === campaign.id ? c.brand.primary : c.border.primary,
                  background: selectedCampaign?.id === campaign.id ? `${c.brand.primary}15` : c.bg.secondary,
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ color: c.text.primary, fontWeight: 600, fontSize: 14 }}>{campaign.name}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 6,
                    background: campaign.status === 'ENABLED' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                    color: campaign.status === 'ENABLED' ? '#10b981' : '#6b7280'
                  }}>{campaign.status}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                  <div><span style={{ color: c.text.tertiary }}>Cost:</span> <span style={{ color: c.text.primary, fontWeight: 500 }}>{formatCurrency(campaign.cost)}</span></div>
                  <div><span style={{ color: c.text.tertiary }}>Conv:</span> <span style={{ color: c.text.primary, fontWeight: 500 }}>{formatNumber(campaign.conversions)}</span></div>
                  <div><span style={{ color: c.text.tertiary }}>ROAS:</span> <span style={{ color: parseFloat(campaign.roas) >= 1 ? '#10b981' : '#ef4444', fontWeight: 500 }}>{campaign.roas}x</span></div>
                  <div><span style={{ color: c.text.tertiary }}>Clicks:</span> <span style={{ color: c.text.primary, fontWeight: 500 }}>{formatNumber(campaign.clicks)}</span></div>
                </div>
              </div>
            ))}
          </div>

          {/* Campaign Detail */}
          {selectedCampaign && (
            <div>
              {/* Campaign Stats */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                <StatCard icon={DollarSign} label="Cost" value={formatCurrency(selectedCampaign.cost)} color="#8b5cf6" theme={theme} />
                <StatCard icon={Target} label="Conversions" value={formatNumber(selectedCampaign.conversions)} color="#10b981" theme={theme} />
                <StatCard icon={TrendingUp} label="ROAS" value={`${selectedCampaign.roas}x`} color={parseFloat(selectedCampaign.roas) >= 1 ? '#10b981' : '#ef4444'} theme={theme} />
                <StatCard icon={MousePointer} label="Clicks" value={formatNumber(selectedCampaign.clicks)} color="#3b82f6" theme={theme} />
                <StatCard icon={Eye} label="Impressions" value={formatNumber(selectedCampaign.impressions)} color="#f59e0b" theme={theme} />
                <StatCard icon={BarChart3} label="CTR" value={`${selectedCampaign.ctr}%`} color="#6366f1" theme={theme} />
              </div>

              {/* Performance Chart (simplified) */}
              {performanceData.length > 0 && (
                <div style={cardStyle}>
                  <h3 style={{ color: c.text.primary, fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Daily Performance</h3>
                  <PerformanceMiniChart data={performanceData} theme={theme} />
                </div>
              )}

              {/* Asset Groups */}
              <div style={cardStyle}>
                <h3 style={{ color: c.text.primary, fontSize: 16, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Layers size={18} /> Asset Groups
                  {loadingGroups && <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                </h3>

                {groupError && (
                  <div style={{ padding: 12, borderRadius: 8, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertCircle size={16} /> {groupError}
                  </div>
                )}
                {assetGroups.length === 0 && !loadingGroups && !groupError ? (
                  <p style={{ color: c.text.tertiary, fontSize: 14 }}>No asset groups found.</p>
                ) : (
                  assetGroups.map(group => {
                    const strengthConfig = AD_STRENGTH_CONFIG[group.adStrength] || AD_STRENGTH_CONFIG.UNSPECIFIED;
                    const StrengthIcon = strengthConfig.icon;
                    const isExpanded = expandedGroup === group.id;

                    return (
                      <div key={group.id} style={{ marginBottom: 12 }}>
                        <div
                          onClick={() => handleGroupExpand(group.id)}
                          style={{
                            background: c.bg.tertiary,
                            borderRadius: 12,
                            border: `1px solid ${isExpanded ? c.brand.primary : c.border.secondary}`,
                            padding: 16,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              {isExpanded ? <ChevronDown size={16} color={c.text.tertiary} /> : <ChevronRight size={16} color={c.text.tertiary} />}
                              <span style={{ color: c.text.primary, fontWeight: 600, fontSize: 14 }}>{group.name}</span>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                                background: strengthConfig.bg, color: strengthConfig.color
                              }}>
                                <StrengthIcon size={12} /> {strengthConfig.label}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                              <span style={{ color: c.text.tertiary }}>Cost: <strong style={{ color: c.text.primary }}>{formatCurrency(group.cost)}</strong></span>
                              <span style={{ color: c.text.tertiary }}>Conv: <strong style={{ color: c.text.primary }}>{formatNumber(group.conversions)}</strong></span>
                              <span style={{ color: c.text.tertiary }}>ROAS: <strong style={{ color: parseFloat(group.roas) >= 1 ? '#10b981' : '#ef4444' }}>{group.roas}x</strong></span>
                            </div>
                          </div>
                        </div>

                        {/* Expanded: Assets */}
                        {isExpanded && (
                          <div style={{ marginTop: 12, marginLeft: 20 }}>
                            {assetError && (
                              <div style={{ padding: 12, borderRadius: 8, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <AlertCircle size={16} /> {assetError}
                              </div>
                            )}
                            {loadingAssets ? (
                              <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                                <Loader size={24} style={{ animation: 'spin 1s linear infinite', color: c.brand.primary }} />
                              </div>
                            ) : assets ? (
                              <>
                                {/* Performance Summary */}
                                <div style={{
                                  background: c.bg.tertiary, borderRadius: 12,
                                  border: `1px solid ${c.border.secondary}`,
                                  padding: 16, marginBottom: 12
                                }}>
                                  <h4 style={{ color: c.text.primary, fontSize: 13, fontWeight: 600, marginBottom: 12, margin: 0 }}>
                                    Asset Performance Overview ({assets.total} assets)
                                  </h4>
                                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                                    {Object.entries(assets.performanceSummary).filter(([k]) => k !== 'total').map(([key, count]) => {
                                      const labelConfig = PERFORMANCE_LABEL_CONFIG[key.toUpperCase()] || PERFORMANCE_LABEL_CONFIG.UNSPECIFIED;
                                      return (
                                        <div key={key} style={{
                                          background: labelConfig.bg, borderRadius: 8,
                                          padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6
                                        }}>
                                          <span style={{ color: labelConfig.color, fontWeight: 700, fontSize: 18 }}>{count}</span>
                                          <span style={{ color: labelConfig.color, fontSize: 12, fontWeight: 500 }}>{labelConfig.label}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Tips */}
                                {tips.length > 0 && (
                                  <div style={{
                                    background: 'rgba(139, 92, 246, 0.08)', borderRadius: 12,
                                    border: '1px solid rgba(139, 92, 246, 0.2)',
                                    padding: 16, marginBottom: 12
                                  }}>
                                    <h4 style={{ color: '#8b5cf6', fontSize: 13, fontWeight: 600, margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <Zap size={14} /> Optimization Tips
                                    </h4>
                                    {tips.map((tip, i) => (
                                      <div key={i} style={{
                                        display: 'flex', alignItems: 'flex-start', gap: 8,
                                        marginBottom: i < tips.length - 1 ? 8 : 0, fontSize: 13
                                      }}>
                                        <span style={{
                                          width: 6, height: 6, borderRadius: '50%', marginTop: 6, flexShrink: 0,
                                          background: tip.priority === 'high' ? '#ef4444' : tip.priority === 'medium' ? '#f59e0b' : '#10b981'
                                        }} />
                                        <span style={{ color: c.text.secondary }}>{tip.message}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Asset Sections */}
                                <AssetSection title="Headlines" icon={Type} assets={assets.assets.headlines} theme={theme} />
                                <AssetSection title="Descriptions" icon={Type} assets={assets.assets.descriptions} theme={theme} />
                                <AssetSection title="Long Headlines" icon={Type} assets={assets.assets.longHeadlines} theme={theme} />
                                <AssetSection title="Images" icon={Image} assets={assets.assets.images} theme={theme} isImage />
                                <AssetSection title="Videos" icon={Video} assets={assets.assets.videos} theme={theme} />
                              </>
                            ) : null}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, theme }) {
  const c = theme.colors;
  return (
    <div style={{
      background: c.bg.tertiary, borderRadius: 12,
      border: `1px solid ${c.border.secondary}`,
      padding: 16, flex: 1, minWidth: 130
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Icon size={14} style={{ color }} />
        <span style={{ color: c.text.tertiary, fontSize: 12, fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ color: c.text.primary, fontSize: 20, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function AssetSection({ title, icon: Icon, assets, theme, isImage = false }) {
  const c = theme.colors;
  if (!assets || assets.length === 0) return null;

  return (
    <div style={{
      background: c.bg.tertiary, borderRadius: 12,
      border: `1px solid ${c.border.secondary}`,
      padding: 16, marginBottom: 12
    }}>
      <h4 style={{ color: c.text.primary, fontSize: 13, fontWeight: 600, margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon size={14} /> {title} ({assets.length})
      </h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {assets.map((asset, i) => {
          const perfConfig = PERFORMANCE_LABEL_CONFIG[asset.performanceLabel] || PERFORMANCE_LABEL_CONFIG.UNSPECIFIED;
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 12px', borderRadius: 8,
              background: c.bg.secondary, border: `1px solid ${c.border.secondary}`
            }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                {isImage && asset.imageUrl ? (
                  <img
                    src={asset.imageUrl}
                    alt={asset.name || 'Asset'}
                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : null}
                <span style={{ color: c.text.primary, fontSize: 13 }}>
                  {asset.text || asset.name || asset.youtubeVideoId || `Asset ${i + 1}`}
                </span>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                background: perfConfig.bg, color: perfConfig.color
              }}>
                {perfConfig.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PerformanceMiniChart({ data, theme }) {
  const c = theme.colors;
  if (!data || data.length === 0) return null;

  const maxCost = Math.max(...data.map(d => parseFloat(d.cost)));
  const maxConv = Math.max(...data.map(d => d.conversions), 1);

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 10, height: 3, borderRadius: 2, background: '#8b5cf6' }} /> Cost
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 10, height: 3, borderRadius: 2, background: '#10b981' }} /> Conversions
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80 }}>
        {data.map((d, i) => {
          const costHeight = maxCost > 0 ? (parseFloat(d.cost) / maxCost) * 70 : 0;
          const convHeight = maxConv > 0 ? (d.conversions / maxConv) * 70 : 0;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, position: 'relative' }}
              title={`${d.date}: €${d.cost} cost, ${d.conversions} conv`}
            >
              <div style={{ display: 'flex', gap: 1, alignItems: 'flex-end', height: 70 }}>
                <div style={{ width: '45%', height: Math.max(costHeight, 2), background: '#8b5cf6', borderRadius: '2px 2px 0 0', opacity: 0.7 }} />
                <div style={{ width: '45%', height: Math.max(convHeight, 2), background: '#10b981', borderRadius: '2px 2px 0 0', opacity: 0.7 }} />
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: c.text.tertiary }}>
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function UpsellScreen({ theme, getToken }) {
  const c = theme.colors;
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/saas/billing/pmax-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: 40, maxWidth: 800, margin: '80px auto',
      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
    }}>
      <div style={{
        width: 80, height: 80, borderRadius: 24,
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 24, boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)'
      }}>
        <Rocket size={40} color="white" />
      </div>

      <h1 style={{ color: c.text.primary, fontSize: 32, fontWeight: 700, marginBottom: 12 }}>
        PMax Optimizer
      </h1>
      <p style={{ color: c.text.secondary, fontSize: 16, maxWidth: 500, lineHeight: 1.6, marginBottom: 32 }}>
        Take full control of your Performance Max campaigns. Analyze asset groups, track asset performance, and get AI-powered optimization tips.
      </p>

      <div style={{
        background: c.bg.secondary, borderRadius: 20,
        border: `1px solid ${c.border.primary}`,
        padding: 32, width: '100%', maxWidth: 500, marginBottom: 32
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, marginBottom: 24 }}>
          <span style={{ color: c.text.primary, fontSize: 48, fontWeight: 700 }}>€49</span>
          <span style={{ color: c.text.tertiary, fontSize: 16 }}>/month</span>
        </div>

        {[
          'PMax campaign dashboard with full metrics',
          'Asset group performance breakdown',
          'Individual asset performance labels',
          'AI-powered asset strength tips',
          'Daily performance tracking charts',
          'Optimization recommendations'
        ].map((feature, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, textAlign: 'left' }}>
            <CheckCircle size={16} style={{ color: '#10b981', flexShrink: 0 }} />
            <span style={{ color: c.text.secondary, fontSize: 14 }}>{feature}</span>
          </div>
        ))}

        <button
          onClick={handleUpgrade}
          disabled={loading}
          style={{
            width: '100%', marginTop: 24, padding: '14px 24px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            color: 'white', border: 'none', borderRadius: 12,
            fontSize: 16, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'opacity 0.2s'
          }}
        >
          {loading ? <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <ArrowUpRight size={18} />}
          {loading ? 'Starting checkout...' : 'Upgrade to PMax Optimizer'}
        </button>
      </div>

      <p style={{ color: c.text.tertiary, fontSize: 12 }}>
        Cancel anytime. Add-on works with any existing subscription tier.
      </p>
    </div>
  );
}
